import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { globalMediaEventEmitter } from '@atlaskit/media-client/global-media-event-emitter';
import type { ExternalImageIdentifier, Identifier } from '@atlaskit/media-client/identifier';
import type { FileDetails } from '@atlaskit/media-client/item';
import type { MediaClient } from '@atlaskit/media-client/media-client';
import type { FileAttributes, MediaTraceContext } from '@atlaskit/media-common/analytics/types';
import { getRandomTelemetryId } from '@atlaskit/media-common/helpers';
import MediaViewer from '@atlaskit/media-viewer/media-viewer-loader';
import type { MediaViewerExtensions } from '@atlaskit/media-viewer/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import { ImageLoadError } from '../ImageLoadError';
import type { MediaCardError } from '../MediaCardError';
import {
	type CardEventProps,
	type CardPreview,
	type FileStateFlags,
	type SharedCardProps,
} from '../types';
import { type SSRStatus } from '../utils/analytics';
import { getDefaultCardDimensions } from '../utils/cardDimensions';
import { generateUniqueId } from '../utils/generateUniqueId';
import { getMediaCardCursor } from '../utils/getMediaCardCursor';
import { isKeyboardFocusEnteringElement } from '../utils/isKeyboardFocusEnteringElement';
import { shouldPerformanceBeSampled, useMediaCardUfoExperience } from '../utils/ufoExperiences';
import { useCurrentValueRef } from '../utils/useCurrentValueRef';
import { usePrevious } from '../utils/usePrevious';
import { CardView } from './cardView';
import { fireOperationalEvent } from './fireOperationalEvent';
import { performanceNow } from './performance';

type ExternalImageCardStatus = 'loading-preview' | 'complete' | 'error';

export interface ExternalImageCardProps extends SharedCardProps, CardEventProps {
	// Instance of MediaClient.
	readonly mediaClient: MediaClient;
	// Instance of identifier.
	readonly identifier: ExternalImageIdentifier;
	// Uses media MediaViewer to preview the media file.
	readonly shouldOpenMediaViewer?: boolean;
	// Media file list to display in Media Viewer.
	readonly mediaViewerItems?: Identifier[];
	// Extensions passed to the Media Viewer this card opens.
	readonly mediaViewerExtensions?: MediaViewerExtensions;
	// Retrieve auth based on a given context.
	readonly contextId?: string;
	// Disable tooltip for the card
	readonly shouldHideTooltip?: boolean;
}

export const ExternalImageCard = ({
	mediaClient,
	appearance = 'auto',
	resizeMode = 'crop',
	disableOverlay = false,
	// Media Feature Flag defaults are defined in @atlaskit/media-common
	featureFlags = {},
	identifier,
	dimensions,
	contextId,
	alt,
	actions,
	shouldOpenMediaViewer,
	selectable,
	selected,
	testId,
	titleBoxBgColor,
	titleBoxIcon,
	shouldHideTooltip,
	mediaViewerItems,
	mediaViewerExtensions,
	onClick,
	onMouseEnter,
	onFocus,
}: ExternalImageCardProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const cardDimensions = dimensions || getDefaultCardDimensions(appearance);
	const internalOccurrenceKey = useMemo(() => generateUniqueId(), []);
	const timeElapsedTillCommenced = useMemo(() => performanceNow(), []);

	// Generate unique traceId for file
	const traceContext = useMemo<MediaTraceContext>(
		() => ({
			traceId: getRandomTelemetryId(),
		}),
		[],
	);

	const fileStateFlagsRef = useRef<FileStateFlags>({
		wasStatusUploading: false,
		wasStatusProcessing: false,
	});

	const shouldSendPerformanceEvent = useMemo(() => shouldPerformanceBeSampled(), []);

	// UFO experience hook - creates a unique experience instance per card
	const ufoExperience = useMediaCardUfoExperience({
		instanceId: internalOccurrenceKey,
		enabled: shouldSendPerformanceEvent,
	});

	const startUfoExperienceRef = useCurrentValueRef(() => {
		ufoExperience.start();
	});

	const [status, setStatus] = useState<ExternalImageCardStatus>('loading-preview');

	const cardPreview = useMemo(
		() => ({
			dataURI: identifier.dataURI,
			orientation: 1,
			source: 'external' as const,
		}),
		[identifier.dataURI],
	);

	const metadata: FileDetails = {
		id: identifier.mediaItemType,
		name: identifier.name || identifier.dataURI,
		mediaType: 'image',
	};

	const fileAttributes: FileAttributes = {
		fileMediatype: 'image',
		fileId: metadata.id,
	};

	// for analytics
	const ssrReliability: SSRStatus = {
		server: { status: 'unknown' },
		client: { status: 'unknown' },
	};

	const [previewDidRender, setPreviewDidRender] = useState(false);
	const [error, setError] = useState<MediaCardError | undefined>();

	const [mediaViewerSelectedItem, setMediaViewerSelectedItemState] = useState<Identifier | null>(
		null,
	);
	// Extension passing added as part of the comments-in-media-viewer work, so it follows that
	// experiment to avoid shipping ungated changes.
	const enabledMediaViewerExtensions =
		mediaViewerExtensions && isExperimentEnabled('cc_comments_media_viewer_sidebar')
			? mediaViewerExtensions
			: undefined;
	const setMediaViewerSelectedItem = (item: Identifier | null) => {
		setMediaViewerSelectedItemState(item);
		enabledMediaViewerExtensions?.onSelectedItemChange?.(item);
	};

	//----------------------------------------------------------------//
	//---------------------- Analytics  ------------------------------//
	//----------------------------------------------------------------//

	const fireOperationalEventRef = useCurrentValueRef(() => {
		const timeElapsedTillEvent = performanceNow();
		const durationSinceCommenced = timeElapsedTillEvent - timeElapsedTillCommenced;

		const performanceAttributes = {
			overall: {
				durationSincePageStart: timeElapsedTillEvent,
				durationSinceCommenced,
			},
		};
		createAnalyticsEvent &&
			fireOperationalEvent(
				createAnalyticsEvent,
				status,
				fileAttributes,
				performanceAttributes,
				ssrReliability,
				error,
				traceContext,
				undefined,
			);
		// External images don't use SSR - no ssrPreviewInfo passed
		ufoExperience.complete(
			status,
			fileAttributes,
			fileStateFlagsRef.current,
			ssrReliability,
			error,
			undefined,
		);
	});

	const fireAbortedEventRef = useCurrentValueRef(() => {
		// UFO won't abort if it's already in a final state (succeeded, failed, aborted, etc)
		ufoExperience.abort({
			fileAttributes,
			fileStateFlags: fileStateFlagsRef?.current,
			ssrReliability: ssrReliability,
		});
	});

	//----------------------------------------------------------------//
	//------------------------ useEffects ----------------------------//
	//----------------------------------------------------------------//

	useEffect(() => {
		startUfoExperienceRef.current();
	}, [startUfoExperienceRef]);

	const prevStatus = usePrevious(status);

	useEffect(() => {
		if (prevStatus !== undefined && status !== prevStatus) {
			fireOperationalEventRef.current();
		}
	}, [fireOperationalEventRef, prevStatus, status]);

	useEffect(() => {
		if (previewDidRender && status === 'loading-preview') {
			setStatus('complete');
		}
	}, [previewDidRender, status]);

	useEffect(() => {
		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			fireAbortedEventRef.current();
		};
	}, [fireAbortedEventRef]);

	//----------------------------------------------------------------//
	//---------------------- Render Functions ------------------------//
	//----------------------------------------------------------------//

	const renderMediaViewer = () => {
		if (!mediaViewerSelectedItem) {
			return;
		}

		return ReactDOM.createPortal(
			<MediaViewer
				collectionName={''}
				items={mediaViewerItems || []}
				mediaClientConfig={mediaClient.config}
				selectedItem={mediaViewerSelectedItem}
				onClose={() => {
					setMediaViewerSelectedItem(null);
				}}
				contextId={contextId}
				featureFlags={featureFlags}
				extensions={enabledMediaViewerExtensions}
			/>,
			document.body,
		);
	};

	//----------------------------------------------------------------//
	//-------------------------- RENDER ------------------------------//
	//----------------------------------------------------------------//

	const mediaCardCursor = getMediaCardCursor(
		false,
		!!shouldOpenMediaViewer,
		status === 'error',
		!!cardPreview,
		metadata.mediaType,
	);

	return (
		<>
			<CardView
				identifier={identifier}
				status={status}
				error={error}
				mediaItemType={identifier.mediaItemType}
				metadata={metadata}
				cardPreview={cardPreview}
				alt={alt}
				resizeMode={resizeMode}
				dimensions={cardDimensions}
				actions={actions}
				selectable={selectable}
				selected={selected}
				onClick={(
					event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
					analyticsEvent?: UIAnalyticsEvent,
				) => {
					if (onClick) {
						const cardEvent = {
							event,
							mediaItemDetails: metadata,
						};
						onClick(cardEvent, analyticsEvent);
					}

					if (shouldOpenMediaViewer) {
						setMediaViewerSelectedItem({
							mediaItemType: 'external-image',
							dataURI: identifier.dataURI,
							name: identifier.name,
						});
					}
				}}
				onMouseEnter={(event: React.MouseEvent<HTMLDivElement>) => {
					onMouseEnter?.({
						event,
						mediaItemDetails: metadata,
					});
				}}
				onFocus={(event: React.FocusEvent<HTMLDivElement>) => {
					if (!isKeyboardFocusEnteringElement(event)) {
						return;
					}
					onFocus?.({
						event,
						mediaItemDetails: metadata,
					});
				}}
				disableOverlay={disableOverlay}
				onDisplayImage={() => {
					const payloadPart = {
						fileId: identifier.dataURI,
						isUserCollection: false,
					};

					globalMediaEventEmitter.emit('media-viewed', {
						viewingLevel: 'minimal',
						...payloadPart,
					});
				}}
				testId={testId}
				titleBoxBgColor={titleBoxBgColor}
				titleBoxIcon={titleBoxIcon}
				onImageError={(newCardPreview?: CardPreview) => {
					// If the dataURI has been replaced, we can dismiss this error
					if (newCardPreview?.dataURI !== cardPreview?.dataURI) {
						return;
					}
					const error = new ImageLoadError(newCardPreview?.source);
					setStatus('error');
					setError(error);
				}}
				onImageLoad={(newCardPreview?: CardPreview) => {
					// If the dataURI has been replaced, we can dismiss this callback
					if (newCardPreview?.dataURI !== cardPreview?.dataURI) {
						return;
					}
					setPreviewDidRender(true);
				}}
				mediaCardCursor={mediaCardCursor}
				shouldHideTooltip={shouldHideTooltip}
			/>
			{mediaViewerSelectedItem ? renderMediaViewer() : null}
		</>
	);
};
